import { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Trash2, Edit2, Wifi, Server, Rocket, Activity, CheckCircle2, XCircle, Loader2, ArrowLeft, ChevronDown, ChevronRight } from 'lucide-react';

const TOKEN_KEY = 'admin_token';

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem(TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function apiFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const base = (import.meta.env.VITE_ADMIN_ENTRY as string) || 'admin';
  return fetch(`/${base}${url}`, { ...options, headers: { ...authHeaders(), ...(options.headers as Record<string, string> || {}) } });
}

interface Host {
  id: number;
  name: string;
  host: string;
  ssh_port: number;
  username: string;
  auth_type: string;
  role: string;
  status: string;
  public_url?: string;
  last_check_at?: string;
}

interface Task {
  id: number;
  host_id: number;
  type: string;
  status: string;
  current_step: string;
  step_index: number;
  step_total: number;
  steps_json: string;
  log: string;
  config_json: string;
  error?: string;
  started_at?: string;
  finished_at?: string;
}

const STATUS_BADGE: Record<string, string> = {
  online: 'text-emerald-400 bg-emerald-500/10',
  offline: 'text-red-400 bg-red-500/10',
  unknown: 'text-gray-400 bg-gray-500/10',
};

const TASK_STATUS_BADGE: Record<string, { color: string; icon: any }> = {
  running: { color: 'text-cyan-400', icon: Loader2 },
  success: { color: 'text-emerald-400', icon: CheckCircle2 },
  failed: { color: 'text-red-400', icon: XCircle },
  pending: { color: 'text-gray-400', icon: Activity },
  cancelled: { color: 'text-yellow-400', icon: XCircle },
};

export function MigrationTab() {
  const [hosts, setHosts] = useState<Host[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [showHostForm, setShowHostForm] = useState(false);
  const [editingHost, setEditingHost] = useState<Host | null>(null);
  const [activeTask, setActiveTask] = useState<Task | null>(null);
  const [view, setView] = useState<'main' | 'progress'>('main');
  const [inferredConfig, setInferredConfig] = useState<any>(null);

  const loadHosts = useCallback(() => {
    apiFetch('/api/admin/migration/hosts').then(r => r.json()).then(d => setHosts(d.data || [])).catch(() => {});
  }, []);

  const loadTasks = useCallback(() => {
    apiFetch('/api/admin/migration/tasks').then(r => r.json()).then(d => setTasks(d.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    loadHosts();
    loadTasks();
    const interval = setInterval(loadTasks, 5000);
    return () => clearInterval(interval);
  }, [loadHosts, loadTasks]);

  return (
    <div>
      {view === 'progress' && activeTask ? (
        <TaskProgressView task={activeTask} inferredConfig={inferredConfig} onBack={() => { setView('main'); setActiveTask(null); setInferredConfig(null); }} onUpdate={loadTasks} />
      ) : (
        <>
          <div className="flex items-center justify-between mb-5">
            <div>
              <h1 className="text-2xl font-bold">项目迁移与冗余</h1>
              <p className="text-xs text-gray-500 mt-1">输入远程主机地址、用户名、密码，一键自动迁移完整项目并安装环境</p>
            </div>
          </div>

          <HostManagement
            hosts={hosts}
            onReload={loadHosts}
            showForm={showHostForm}
            setShowForm={setShowHostForm}
            editingHost={editingHost}
            setEditingHost={setEditingHost}
            onMigrate={async (host) => {
              if (!confirm(`确认一键迁移到 ${host.name}（${host.host}）？\n将自动连接远程主机，安装完整环境并同步所有数据。`)) return;
              try {
                const r = await apiFetch('/api/admin/migration/tasks/start', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ hostId: host.id }),
                });
                const d = await r.json();
                if (!r.ok) { alert(d.error || '启动失败'); return; }
                setInferredConfig(d.config);
                setActiveTask({ id: d.taskId, status: 'running', step_index: 0, step_total: 0, log: '', steps_json: '[]', current_step: '', config_json: '{}', host_id: host.id, type: 'migrate' });
                setView('progress');
                loadTasks();
              } catch (e: any) {
                alert(e.message || '请求失败');
              }
            }}
          />

          <TaskHistory tasks={tasks} onView={(t) => { setActiveTask(t); setView('progress'); }} onReload={loadTasks} />
        </>
      )}
    </div>
  );
}

// ── 主机管理（含一键迁移）──

function HostManagement({ hosts, onReload, showForm, setShowForm, editingHost, setEditingHost, onMigrate }: {
  hosts: Host[];
  onReload: () => void;
  showForm: boolean;
  setShowForm: (v: boolean) => void;
  editingHost: Host | null;
  setEditingHost: (h: Host | null) => void;
  onMigrate: (h: Host) => void;
}) {
  const [testing, setTesting] = useState<number | null>(null);
  const [testResult, setTestResult] = useState<Record<number, { online: boolean; error?: string }>>({});
  const [migrating, setMigrating] = useState<number | null>(null);

  const handleTest = async (id: number) => {
    setTesting(id);
    try {
      const r = await apiFetch(`/api/admin/migration/hosts/${id}/test`, { method: 'POST' });
      const d = await r.json();
      setTestResult({ ...testResult, [id]: d });
      onReload();
    } catch {}
    setTesting(null);
  };

  const handleDelete = async (id: number) => {
    if (!confirm('确认删除此主机？')) return;
    await apiFetch(`/api/admin/migration/hosts/${id}`, { method: 'DELETE' });
    onReload();
  };

  const handleMigrate = async (h: Host) => {
    setMigrating(h.id);
    await onMigrate(h);
    setMigrating(null);
  };

  return (
    <div className="rounded-xl border border-gray-700/60 bg-gray-900/40 p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold flex items-center gap-2"><Server size={18} className="text-cyan-400" /> 目标主机</h2>
        <button onClick={() => { setEditingHost(null); setShowForm(true); }} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30 text-sm">
          <Plus size={14} /> 添加主机
        </button>
      </div>

      {showForm && <HostForm host={editingHost} onDone={() => { setShowForm(false); setEditingHost(null); onReload(); }} onCancel={() => { setShowForm(false); setEditingHost(null); }} />}

      {hosts.length === 0 && !showForm ? (
        <div className="text-center py-12">
          <Server size={32} className="mx-auto text-gray-600 mb-3" />
          <p className="text-sm text-gray-500 mb-1">尚未添加目标主机</p>
          <p className="text-xs text-gray-600">点击「添加主机」，填入主机地址、用户名、密码即可</p>
        </div>
      ) : (
        <div className="space-y-3">
          {hosts.map(h => (
            <div key={h.id} className="rounded-lg border border-gray-800 bg-gray-900/60 p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-200">{h.name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_BADGE[h.status] || STATUS_BADGE.unknown}`}>{h.status}</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">{h.username}@{h.host}:{h.ssh_port}</div>
                  {testResult[h.id] && (
                    <div className={`text-xs mt-1 ${testResult[h.id].online ? 'text-emerald-400' : 'text-red-400'}`}>
                      {testResult[h.id].online ? '✓ SSH 连接成功' : `✗ ${testResult[h.id].error || '连接失败'}`}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 ml-2">
                  <button onClick={() => handleTest(h.id)} disabled={testing === h.id} className="p-2 rounded-lg text-gray-400 hover:text-cyan-400 hover:bg-gray-800" title="测试 SSH 连接">
                    {testing === h.id ? <Loader2 size={15} className="animate-spin" /> : <Wifi size={15} />}
                  </button>
                  <button onClick={() => { setEditingHost(h); setShowForm(true); }} className="p-2 rounded-lg text-gray-400 hover:text-cyan-400 hover:bg-gray-800" title="编辑">
                    <Edit2 size={15} />
                  </button>
                  <button onClick={() => handleDelete(h.id)} className="p-2 rounded-lg text-gray-400 hover:text-red-400 hover:bg-gray-800" title="删除">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
              <button
                onClick={() => handleMigrate(h)}
                disabled={migrating === h.id}
                className="w-full mt-3 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-cyan-500 text-white hover:bg-cyan-600 disabled:opacity-50 text-sm font-medium transition"
              >
                {migrating === h.id ? <Loader2 size={15} className="animate-spin" /> : <Rocket size={15} />}
                一键迁移到此主机
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── 主机表单（简化：只需主机地址、用户名、密码）──

function HostForm({ host, onDone, onCancel }: { host: Host | null; onDone: () => void; onCancel: () => void }) {
  const [form, setForm] = useState({
    host: host?.host || '',
    username: host?.username || 'root',
    password: '',
    port: host?.ssh_port || 22,
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [authType, setAuthType] = useState(host?.auth_type || 'password');
  const [error, setError] = useState('');

  const handleSave = async () => {
    setError('');
    if (!form.host) { setError('主机地址不能为空'); return; }
    if (!form.username) { setError('用户名不能为空'); return; }
    if (!host && !form.password) { setError('密码不能为空'); return; }

    const name = host?.name || form.host;
    const body: any = {
      name, host: form.host, sshPort: form.port, username: form.username,
      authType, role: 'target', publicUrl: '',
    };
    if (form.password) body.credential = form.password;

    const r = host
      ? await apiFetch(`/api/admin/migration/hosts/${host.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      : await apiFetch('/api/admin/migration/hosts', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    const d = await r.json();
    if (!r.ok) { setError(d.error || '保存失败'); return; }
    onDone();
  };

  return (
    <div className="mb-4 rounded-lg border border-cyan-500/30 bg-cyan-500/5 p-4 space-y-3">
      <div className="space-y-3">
        <div>
          <label className="text-xs text-gray-400">主机地址 *</label>
          <input value={form.host} onChange={e => setForm({ ...form, host: e.target.value })} placeholder="如: 192.168.1.100 或 example.com" className="w-full mt-1 rounded-lg bg-gray-800 border border-gray-700 px-3 py-2 text-sm text-gray-200" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-400">用户名 *</label>
            <input value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} placeholder="root" className="w-full mt-1 rounded-lg bg-gray-800 border border-gray-700 px-3 py-2 text-sm text-gray-200" />
          </div>
          <div>
            <label className="text-xs text-gray-400">{authType === 'password' ? '密码 *' : '私钥 *'} {host && '（留空不修改）'}</label>
            {authType === 'password' ? (
              <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="SSH 密码" className="w-full mt-1 rounded-lg bg-gray-800 border border-gray-700 px-3 py-2 text-sm text-gray-200" />
            ) : (
              <textarea value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} rows={3} placeholder="-----BEGIN OPENSSH PRIVATE KEY-----..." className="w-full mt-1 rounded-lg bg-gray-800 border border-gray-700 px-3 py-2 text-sm text-gray-200 font-mono" />
            )}
          </div>
        </div>

        <button onClick={() => setShowAdvanced(!showAdvanced)} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-300">
          {showAdvanced ? <ChevronDown size={12} /> : <ChevronRight size={12} />} 高级设置
        </button>
        {showAdvanced && (
          <div className="grid grid-cols-2 gap-3 pt-1">
            <div>
              <label className="text-xs text-gray-400">SSH 端口</label>
              <input type="number" value={form.port} onChange={e => setForm({ ...form, port: Number(e.target.value) || 22 })} className="w-full mt-1 rounded-lg bg-gray-800 border border-gray-700 px-3 py-2 text-sm text-gray-200" />
            </div>
            <div>
              <label className="text-xs text-gray-400">认证方式</label>
              <select value={authType} onChange={e => setAuthType(e.target.value)} className="w-full mt-1 rounded-lg bg-gray-800 border border-gray-700 px-3 py-2 text-sm text-gray-200">
                <option value="password">密码</option>
                <option value="key">私钥</option>
              </select>
            </div>
          </div>
        )}
      </div>
      {error && <p className="text-sm text-red-400">{error}</p>}
      <div className="flex gap-2">
        <button onClick={handleSave} className="px-4 py-2 rounded-lg bg-cyan-500 text-white hover:bg-cyan-600 text-sm">保存</button>
        <button onClick={onCancel} className="px-4 py-2 rounded-lg bg-gray-800 text-gray-400 hover:bg-gray-700 text-sm">取消</button>
      </div>
    </div>
  );
}

// ── 任务进度视图 ──

function TaskProgressView({ task, inferredConfig, onBack, onUpdate }: { task: Task; inferredConfig: any; onBack: () => void; onUpdate: () => void }) {
  const [currentTask, setCurrentTask] = useState<Task>(task);
  const [logLines, setLogLines] = useState<string[]>(task.log ? task.log.split('\n').filter(Boolean) : []);
  const [stepStatuses, setStepStatuses] = useState<Record<string, 'running' | 'success' | 'failed'>>({});
  const logRef = useRef<HTMLDivElement>(null);
  const finishedRef = useRef(false);

  useEffect(() => {
    setCurrentTask(task);
    setLogLines(task.log ? task.log.split('\n').filter(Boolean) : []);
  }, [task]);

  useEffect(() => {
    const controller = new AbortController();
    let lastLogLen = logLines.length;

    async function streamProgress() {
      try {
        const base = (import.meta.env.VITE_ADMIN_ENTRY as string) || 'admin';
        const resp = await fetch(`/${base}/api/admin/migration/tasks/${task.id}/progress`, {
          headers: authHeaders(),
          signal: controller.signal,
        });
        const reader = resp.body!.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                if (data.type === 'snapshot' && data.task) {
                  setCurrentTask(data.task);
                  const allLines = data.task.log ? data.task.log.split('\n').filter(Boolean) : [];
                  if (allLines.length > lastLogLen) {
                    setLogLines(prev => [...prev, ...allLines.slice(lastLogLen)]);
                    lastLogLen = allLines.length;
                  }
                } else if (data.type === 'log') {
                  setLogLines(prev => [...prev, data.line]);
                  lastLogLen++;
                } else if (data.type === 'step') {
                  setStepStatuses(prev => ({ ...prev, [data.stepKey]: data.status }));
                } else if (data.type === 'done') {
                  if (!finishedRef.current) {
                    finishedRef.current = true;
                    onUpdate();
                    setTimeout(() => {
                      apiFetch(`/api/admin/migration/tasks/${task.id}`).then(r => r.json()).then(d => { if (d.data) setCurrentTask(d.data); });
                    }, 500);
                  }
                }
              } catch {}
            }
          }
        }
      } catch {}
    }

    streamProgress();
    return () => controller.abort();
  }, [task.id, onUpdate]);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [logLines]);

  const steps: Array<{ key: string; label: string }> = currentTask.steps_json ? JSON.parse(currentTask.steps_json) : [];
  const isRunning = currentTask.status === 'running';
  const isDone = currentTask.status === 'success' || currentTask.status === 'failed' || currentTask.status === 'cancelled';
  const statusInfo = TASK_STATUS_BADGE[currentTask.status] || TASK_STATUS_BADGE.pending;
  const StatusIcon = statusInfo.icon;

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-2 rounded-lg bg-gray-800 text-gray-400 hover:text-gray-200">
            <ArrowLeft size={16} />
          </button>
          <div>
            <h1 className="text-2xl font-bold">迁移进度</h1>
            <p className="text-xs text-gray-500 mt-0.5">任务 #{currentTask.id}</p>
          </div>
        </div>
        <div className={`flex items-center gap-2 text-sm font-medium ${statusInfo.color}`}>
          <StatusIcon size={16} className={isRunning ? 'animate-spin' : ''} />
          {currentTask.status === 'running' ? '进行中' : currentTask.status === 'success' ? '已完成' : currentTask.status === 'failed' ? '失败' : currentTask.status === 'cancelled' ? '已取消' : '等待中'}
        </div>
      </div>

      {/* 自动推断的配置信息 */}
      {inferredConfig && (
        <div className="rounded-xl border border-cyan-500/30 bg-cyan-500/5 p-4 mb-4">
          <h3 className="text-sm font-medium text-cyan-300 mb-2">自动配置信息（请记录迁移后的管理员密码）</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <ConfigItem label="域名" value={inferredConfig.domain} />
            <ConfigItem label="管理员密码" value={inferredConfig.adminPass} highlight />
            <ConfigItem label="管理员邮箱" value={inferredConfig.adminEmail} />
            <ConfigItem label="数据库" value={inferredConfig.dbType} />
            <ConfigItem label="安装目录" value={inferredConfig.remoteDir} />
            <ConfigItem label="双机冗余" value={inferredConfig.enableHa ? '已启用' : '未启用'} />
          </div>
        </div>
      )}

      {/* 步骤进度条 */}
      <div className="rounded-xl border border-gray-700/60 bg-gray-900/40 p-5 mb-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm text-gray-400">步骤进度</span>
          <span className="text-sm text-gray-300">{currentTask.step_index}/{currentTask.step_total}</span>
        </div>
        <div className="w-full bg-gray-800 rounded-full h-2 mb-4">
          <div className="bg-cyan-500 h-2 rounded-full transition-all" style={{ width: `${currentTask.step_total > 0 ? (currentTask.step_index / currentTask.step_total) * 100 : 0}%` }} />
        </div>
        <div className="space-y-2">
          {steps.map((step, i) => {
            const status = stepStatuses[step.key] || (i < currentTask.step_index ? 'success' : i === currentTask.step_index && isRunning ? 'running' : '');
            const icon = status === 'success' ? CheckCircle2 : status === 'failed' ? XCircle : status === 'running' ? Loader2 : Activity;
            const Icon = icon;
            const color = status === 'success' ? 'text-emerald-400' : status === 'failed' ? 'text-red-400' : status === 'running' ? 'text-cyan-400' : 'text-gray-600';
            return (
              <div key={step.key} className={`flex items-center gap-2 text-sm ${color}`}>
                <Icon size={14} className={status === 'running' ? 'animate-spin' : ''} />
                <span>{step.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* 日志 */}
      <div className="rounded-xl border border-gray-700/60 bg-gray-900/40 p-5">
        <h3 className="text-sm font-medium text-gray-400 mb-3">执行日志</h3>
        <div ref={logRef} className="bg-black/40 rounded-lg p-3 font-mono text-xs text-gray-300 max-h-96 overflow-y-auto">
          {logLines.length === 0 ? <span className="text-gray-600">等待日志输出...</span> : logLines.map((line, i) => (
            <div key={i} className={`whitespace-pre-wrap ${line.startsWith('✗') ? 'text-red-400' : line.startsWith('✓') ? 'text-emerald-400' : line.startsWith('━━━') ? 'text-cyan-400 font-bold' : ''}`}>{line}</div>
          ))}
        </div>
      </div>

      {isDone && (
        <div className="mt-4 flex justify-center">
          <button onClick={onBack} className="px-6 py-2 rounded-lg bg-gray-800 text-gray-300 hover:bg-gray-700 text-sm">返回</button>
        </div>
      )}
    </div>
  );
}

function ConfigItem({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div>
      <div className="text-gray-500">{label}</div>
      <div className={`mt-0.5 font-mono break-all ${highlight ? 'text-yellow-400 font-bold' : 'text-gray-300'}`}>{value || '-'}</div>
    </div>
  );
}

// ── 任务历史 ──

function TaskHistory({ tasks, onView, onReload }: { tasks: Task[]; onView: (t: Task) => void; onReload: () => void }) {
  const handleCancel = async (id: number) => {
    if (!confirm('确认取消此任务？')) return;
    await apiFetch(`/api/admin/migration/tasks/${id}/cancel`, { method: 'POST' });
    onReload();
  };

  if (tasks.length === 0) return null;

  return (
    <div className="mt-6 rounded-xl border border-gray-700/60 bg-gray-900/40 p-5">
      <h2 className="text-lg font-semibold flex items-center gap-2 mb-4"><Activity size={18} className="text-cyan-400" /> 迁移历史</h2>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 border-b border-gray-800">
              <th className="py-2 pr-4">#</th>
              <th className="py-2 pr-4">主机</th>
              <th className="py-2 pr-4">状态</th>
              <th className="py-2 pr-4">进度</th>
              <th className="py-2 pr-4">开始时间</th>
              <th className="py-2 pr-4">操作</th>
            </tr>
          </thead>
          <tbody>
            {tasks.map(t => {
              const info = TASK_STATUS_BADGE[t.status] || TASK_STATUS_BADGE.pending;
              const Icon = info.icon;
              return (
                <tr key={t.id} className="border-b border-gray-800/50">
                  <td className="py-2 pr-4 text-gray-400">{t.id}</td>
                  <td className="py-2 pr-4 text-gray-300">主机 #{t.host_id}</td>
                  <td className={`py-2 pr-4 ${info.color}`}>
                    <span className="flex items-center gap-1"><Icon size={12} className={t.status === 'running' ? 'animate-spin' : ''} />{t.status}</span>
                  </td>
                  <td className="py-2 pr-4 text-gray-400">{t.step_index}/{t.step_total}</td>
                  <td className="py-2 pr-4 text-gray-500 text-xs">{t.started_at || '-'}</td>
                  <td className="py-2 pr-4">
                    <div className="flex gap-2">
                      <button onClick={() => onView(t)} className="text-xs text-cyan-400 hover:text-cyan-300">查看</button>
                      {t.status === 'running' && <button onClick={() => handleCancel(t.id)} className="text-xs text-red-400 hover:text-red-300">取消</button>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}